// src/store/teamStore.ts
/**
 * .tuMetrics {"tu1": 0.9, "tu2": 0.92, "tu3": 5, "notes": "Browse filters are per-view; selectedTeamId only locks team guests", "tuVersion": "2.5"}
 * .help "Loads company teams. selectedTeamId is not a global browse mode — views use TeamFilterChip locally. Team-access guests stay locked to their assigned team."
 */
import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import useUserStore from './userStore';
import useFeedStore from './feedStore';
import type { Database } from '../types/supabase';
import { Team as TeamType } from '../types'; // Import TeamType from types/index.ts

/** Legacy keys from the global Company/team browse selector — cleared on init. */
const getTeamSelectionStorageKey = (companyId: string) => `selectedTeamId:${companyId}`;

type TeamRow = Database['public']['Tables']['teams']['Row'];
type TeamInsert = Database['public']['Tables']['teams']['Insert'];
type TeamUpdate = Database['public']['Tables']['teams']['Update'];

// Map database row to frontend Team type
const mapTeamFromDB = (row: TeamRow): TeamType => ({
  id: row.id,
  name: row.name,
  description: row.description || undefined,
  createdAt: row.created_at,
  companyId: row.company_id || undefined, // Map company_id
  // November 20, 2025: Google Drive integration
  shared_drive_id: row.shared_drive_id || undefined,
  shared_drive_name: row.shared_drive_name || undefined,
  visibility: row.visibility || undefined,
  is_private: row.is_private || undefined,
  encryption_enabled: row.encryption_enabled || undefined,
  encryption_provider: row.encryption_provider || undefined,
  updated_at: row.updated_at || undefined,
});

interface TeamState {
  teams: TeamType[];
  isLoading: boolean;
  error: string | null;
  /**
   * @deprecated Not a browse filter. Null for normal users; team-access guests are locked to their team.
   * Views use local TeamFilterChip state instead.
   */
  selectedTeamId: string | null;

  initializeTeams: () => Promise<void>;
  /** @deprecated Browse filters are per-view. Only enforces team-guest lock. */
  setSelectedTeamId: (teamId: string | null) => void;
  addTeam: (name: string, description?: string, isPrivate?: boolean) => Promise<void>;
  updateTeam: (teamId: string, updates: { name?: string; description?: string; is_private?: boolean }) => Promise<void>;
  deleteTeam: (teamId: string) => Promise<void>;
}

const useTeamStore = create<TeamState>((set, get) => ({
  teams: [],
  isLoading: false,
  error: null,
  selectedTeamId: null, // September 8, 2025 2:30 PM: Initialize selectedTeamId to null

  initializeTeams: async () => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) {
        console.warn('initializeTeams: No current user companyId found. Skipping team initialization.');
        set({ teams: [], isLoading: false });
        return;
      }

      const { data, error } = await supabase
        .from('teams')
        .select('*')
        .eq('company_id', currentUser.companyId) // Filter by current user's company_id
        .order('created_at', { ascending: true }); // Order by creation time to preserve creation order

      if (error) throw error;

      let teams = data.map(mapTeamFromDB);

      // November 22, 2025: Filter teams for team-level guests
      if (currentUser.isGuest && currentUser.guestAccessLevel === 'team') {
        // Team guests only see their assigned team
        const userTeamId = currentUser.team;
        teams = teams.filter(t => t.id === userTeamId);
      }

      // Ensure Company team is first (it should exist from SQL script)
      const companyTeamIndex = teams.findIndex(t => t.name === 'Company');
      if (companyTeamIndex > 0) {
        // Move Company team to beginning if it exists
        const [companyTeam] = teams.splice(companyTeamIndex, 1);
        teams.unshift(companyTeam);
      }

      // Clear legacy global browse-filter keys so old Company/Marketing selections
      // cannot poison 1.0 or 2.0 lists after this fix.
      if (currentUser.companyId) {
        localStorage.removeItem(getTeamSelectionStorageKey(currentUser.companyId));
      }
      localStorage.removeItem('selectedTeamId');

      // Team-access guests stay locked to their assigned team for any leftover callers.
      // Everyone else: null (= All teams). Browse filters live on each view.
      const initialSelectedTeamId =
        currentUser.isGuest && currentUser.guestAccessLevel === 'team'
          ? (currentUser.team || null)
          : null;

      set({ teams, isLoading: false, selectedTeamId: initialSelectedTeamId });
    } catch (error) {
      console.error('Error initializing teams:', error);
      set({ error: 'Failed to load teams', isLoading: false });
    }
  },

  setSelectedTeamId: (teamId) => {
    const currentUser = useUserStore.getState().currentUser;
    if (currentUser?.isGuest && currentUser?.guestAccessLevel === 'team') {
      console.warn('Team guests cannot change team selection - locked to assigned team');
      return;
    }
    // No-op for browse mode: views own their TeamFilterChip state.
    // Kept so admin/tests that still call this do not crash.
    set({ selectedTeamId: teamId });
  },

  addTeam: async (name, description, isPrivate = false) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');
      if (!currentUser.companyId) throw new Error('Current user is not associated with a company.');

      const teamData: TeamInsert = {
        name,
        description: description || null,
        company_id: currentUser.companyId, // Assign current user's company_id
        is_private: isPrivate,
      };

      const { data, error } = await supabase
        .from('teams')
        .insert(teamData)
        .select()
        .single();

      if (error) throw error;

      const newTeam = mapTeamFromDB(data);

      // IMPORTANT: For private teams, assign the creator to the team
      // This ensures they can see the team they just created
      if (isPrivate) {
        const { error: membershipError } = await supabase
          .from('memberships')
          .update({ team_id: newTeam.id })
          .eq('user_id', currentUser.id)
          .eq('company_id', currentUser.companyId);

        if (membershipError) {
          console.error('Error assigning creator to private team:', membershipError);
          // Continue anyway - admin can fix manually
        }
      }

      if (currentUser) { // Only add feed event if a user is logged in
        await useFeedStore.getState().addEvent({
          type: 'team_update',
          title: 'New Team Created',
          description: `${currentUser.name} created team "${newTeam.name}"${isPrivate ? ' (Private)' : ''}`,
          userId: currentUser.id, // Recipient of this notification (the creator)
          initiatorId: currentUser.id, // The user who initiated the action
        });
      }

      set((state) => ({
        teams: [...state.teams, newTeam],
        isLoading: false,
      }));
    } catch (error) {
      console.error('Error adding team:', error);
      set({ error: 'Failed to add team', isLoading: false });
    }
  },

  updateTeam: async (teamId, updates) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');
      if (!currentUser.companyId) throw new Error('Current user is not associated with a company.');

      const teamToUpdate = get().teams.find(t => t.id === teamId);
      if (!teamToUpdate) throw new Error('Team not found');

      const teamData: TeamUpdate = {
        name: updates.name,
        description: updates.description,
        is_private: updates.is_private,
        company_id: currentUser.companyId, // Ensure company_id is included in updates
      };

      const { data, error } = await supabase
        .from('teams')
        .update(teamData)
        .eq('id', teamId)
        .eq('company_id', currentUser.companyId) // CRITICAL: Scope to company
        .select()
        .single();

      if (error) throw error;

      const updatedTeam = mapTeamFromDB(data);

      if (currentUser) { // Only add feed event if a user is logged in
        await useFeedStore.getState().addEvent({
          type: 'team_update',
          title: 'Team Updated',
          description: `${currentUser.name} updated team "${updatedTeam.name}"`,
          userId: currentUser.id, // Recipient of this notification (the updater)
          initiatorId: currentUser.id, // The user who initiated the action
        });
      }

      set((state) => ({
        teams: state.teams.map((team) =>
          team.id === teamId ? updatedTeam : team
        ),
        isLoading: false,
      }));
    } catch (error) {
      console.error('Error updating team:', error);
      set({ error: 'Failed to update team', isLoading: false });
    }
  },

  deleteTeam: async (teamId) => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser) throw new Error('No authenticated user');
      if (!currentUser.companyId) throw new Error('Current user is not associated with a company.');

      const teamToDelete = get().teams.find(t => t.id === teamId);
      if (!teamToDelete) throw new Error('Team not found');

      // Set team_id to null for all associated users within the same company
      const { error: userUpdateError } = await supabase
        .from('users')
        .update({ team_id: null })
        .eq('team_id', teamId)
        .eq('company_id', currentUser.companyId); // Filter by company_id
      if (userUpdateError) console.error('Error updating users for deleted team:', userUpdateError);

      // Set team_id to null for all associated cards within the same company
      const { error: cardUpdateError } = await supabase
        .from('cards')
        .update({ team_id: null })
        .eq('team_id', teamId)
        .eq('company_id', currentUser.companyId); // Filter by company_id
      if (cardUpdateError) console.error('Error updating cards for deleted team:', cardUpdateError);

      // Set team_id to null for all associated sprints within the same company
      const { error: sprintUpdateError } = await supabase
        .from('sprints')
        .update({ team_id: null })
        .eq('team_id', teamId)
        .eq('company_id', currentUser.companyId); // Filter by company_id
      if (sprintUpdateError) console.error('Error updating sprints for deleted team:', sprintUpdateError);

      // Set team_id to null for all associated roadmaps within the same company
      const { error: roadmapUpdateError } = await supabase
        .from('roadmaps')
        .update({ team_id: null })
        .eq('team_id', teamId)
        .eq('company_id', currentUser.companyId); // Filter by company_id
      if (roadmapUpdateError) console.error('Error updating roadmaps for deleted team:', roadmapUpdateError);

      // Now delete the team itself
      const { error } = await supabase
        .from('teams')
        .delete()
        .eq('id', teamId)
        .eq('company_id', currentUser.companyId); // Filter by company_id
      if (error) throw error;

      if (currentUser) { // Only add feed event if a user is logged in
        await useFeedStore.getState().addEvent({
          type: 'team_update',
          title: 'Team Deleted',
          description: `${currentUser.name} deleted team "${teamToDelete.name}"`,
          userId: currentUser.id, // Recipient of this notification (the deleter)
          initiatorId: currentUser.id, // The user who initiated the action
        });
      }

      set((state) => ({
        teams: state.teams.filter((team) => team.id !== teamId),
        isLoading: false,
      }));
    } catch (error) {
      console.error('Error deleting team:', error);
      set({ error: 'Failed to delete team', isLoading: false });
    }
  },
}));

export default useTeamStore;

